import { Link } from "react-router-dom";
import { HiArrowRight } from "react-icons/hi2";
import admissionProcessHeroBg from "../assets/admissionprocesshero.png";
import applicationFormImage from "../assets/applicationform1.png";

const steps = [
  {
    id: "enquire",
    step: 1,
    title: "Enquire or apply",
    text: "Share your child’s grade, goals, and timeline. You can start with a short form or speak with our admissions team.",
  },
  {
    id: "counsel",
    step: 2,
    title: "Counselling & stream fit",
    text: "We confirm board options, stream choice (Commerce, Science, or Arts), and subject combinations that match university plans.",
  },
  {
    id: "documents",
    step: 3,
    title: "Documents & verification",
    text: "Submit prior school records and ID as requested. We verify eligibility and outline any credit transfer or bridging steps.",
  },
  {
    id: "enrol",
    step: 4,
    title: "Enrolment & onboarding",
    text: "Complete enrolment, receive your schedule and learning access, and meet your academic point of contact.",
  },
];

function AdmissionProcess() {
  return (
    <div className="bg-white">
      <section
        className="relative isolate flex min-h-[180px] items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat sm:min-h-[200px] md:min-h-[220px]"
        style={{ backgroundImage: `url(${admissionProcessHeroBg})` }}
        aria-labelledby="admission-process-hero-heading"
      >
        <div
          className="absolute inset-0 bg-gradient-to-r from-ink/70 via-ink/50 to-ink/35"
          aria-hidden
        />
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-6 text-center sm:px-8 sm:py-8 md:px-10 lg:px-8">
          <h1
            id="admission-process-hero-heading"
            className="text-3xl font-semibold tracking-tight text-white sm:text-4xl md:text-5xl"
          >
            Admission Process
          </h1>
        </div>
      </section>

      <section
        className="relative overflow-hidden bg-gradient-to-b from-white via-brand-light/35 to-brand-light/55 py-16 sm:py-20 lg:py-24"
        aria-labelledby="admission-process-page-heading"
      >
        <div
          className="pointer-events-none absolute right-0 top-1/2 h-80 w-80 -translate-y-1/2 translate-x-1/3 rounded-full bg-brand/[0.06] blur-3xl"
          aria-hidden
        />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 md:px-10 lg:px-8">
          <header className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand sm:text-[0.8125rem]">
              Admissions
            </p>
            <h2
              id="admission-process-page-heading"
              className="mt-4 text-balance text-3xl font-bold tracking-tight text-ink sm:mt-5 sm:text-4xl md:text-[2.375rem] md:leading-tight"
            >
              Your admission journey
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-pretty text-base leading-relaxed text-ink/75 sm:mt-6">
              Follow these simple steps to join Altus Online School. Our team
              supports you from first enquiry to your first day of classes.
            </p>
          </header>

          <ol className="relative mx-auto mt-14 max-w-3xl space-y-0 sm:mt-16">
            {steps.map((item, index) => (
              <li
                key={item.id}
                className="relative flex gap-5 pb-10 pl-2 sm:gap-8 sm:pb-12 last:pb-0"
              >
                {index < steps.length - 1 ? (
                  <div
                    className="absolute left-[1.125rem] top-10 bottom-0 w-px bg-gradient-to-b from-brand/35 via-brand/20 to-transparent sm:left-5"
                    aria-hidden
                  />
                ) : null}
                <div className="relative z-[1] flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand to-brand-dark text-sm font-bold text-white shadow-md shadow-brand/25 sm:h-11 sm:w-11 sm:text-base">
                  {item.step}
                </div>
                <div className="min-w-0 pt-0.5">
                  <h3 className="text-lg font-bold tracking-tight text-ink sm:text-xl">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-pretty text-sm leading-relaxed text-ink/72 sm:text-[0.9375rem]">
                    {item.text}
                  </p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mx-auto mt-12 flex max-w-3xl flex-col items-stretch justify-center gap-3 sm:mt-14 sm:flex-row sm:gap-4">
            <Link
              to="https://forms.gle/m1PLhs8jVYXHs1cWA"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-brand px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand/25 transition hover:bg-brand-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:text-base"
            >
              Start application
              <HiArrowRight className="h-5 w-5" aria-hidden />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-2xl border-2 border-brand/25 bg-white px-6 py-3.5 text-sm font-semibold text-brand transition hover:border-brand/40 hover:bg-brand-light/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:text-base"
            >
              Talk to admissions
            </Link>
          </div>
        </div>
      </section>

      <section
        className="relative overflow-hidden bg-white py-16 sm:py-20"
        aria-labelledby="admission-experience-heading"
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-brand-light/25 to-transparent"
          aria-hidden
        />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 md:px-10 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-center lg:gap-14">
            <div className="min-w-0 max-w-3xl">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-brand sm:text-[0.8125rem]">
              The application
              </p>
              <h2
                id="admission-experience-heading"
                className="mt-3 text-balance text-3xl font-bold tracking-tight text-ink sm:text-4xl"
              >
                Save your precious year
              </h2>
              <p className="mt-4 text-pretty text-base leading-relaxed text-ink/70 sm:text-lg">
                Our school is built to serve students that are looking for an
                unconventional approach to high school. Past students and parents
                tell us that our online school is a life changing experience for
                students who struggled in traditional schooling, those pursuing
                entrance exam preparation (IIT and NEET), artistic and athletic
                students, homeschooled children, students with learning
                disabilities, amongst others.
              </p>
              <p className="mt-4 text-pretty text-base leading-relaxed text-ink/70 sm:text-lg">
                If you&apos;ve decided that our school is a good fit for you, click
                the link below and apply with us today. We look forward to getting
                to know you.
              </p>
              <div className="mt-8">
                <Link
                  to="https://forms.gle/m1PLhs8jVYXHs1cWA"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-brand px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand/25 transition hover:bg-brand-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand sm:text-base"
                >
                  Apply now
                  <HiArrowRight className="h-5 w-5" aria-hidden />
                </Link>
              </div>
            </div>

            <div className="mx-auto w-full max-w-md lg:max-w-none">
              <div className="overflow-hidden rounded-2xl border border-brand/10 bg-brand-light/30 shadow-[0_18px_44px_-16px_rgba(30,22,51,0.14)] ring-1 ring-brand/5">
                <img
                  src={applicationFormImage}
                  alt="Students reading near a school bus"
                  className="h-full w-full object-cover object-center"
                  width={1024}
                  height={768}
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default AdmissionProcess;
